/**
 * Events pushed by the desktop shell: native menu items, deep links (hub://...), and files picked through a host
 * integration. The web and extension hosts have none of these, so every subscription is optional.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { create } from "zustand";
import { getEngine, getHost } from "@/engine/transport";
import { toast } from "@/ui";
import { useCommandBarStore, useLayoutStore } from "./navigation";

interface PendingImportState {
  file: { name: string; content: string; kind: "csv" | "linkedin" } | null;
  set(file: PendingImportState["file"]): void;
  clear(): void;
}

/** A picked file waits here until the import panel mounts and consumes it. */
export const usePendingImport = create<PendingImportState>((set) => ({
  file: null,
  set: (file) => set({ file }),
  clear: () => set({ file: null }),
}));

/** hub://contact/<id>, hub://invite/<token>, hub://people, hub://settings/import ... -> in-app route. */
export function routeForDeepLink(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "hub:") return null;
  const parts = [url.hostname, ...url.pathname.split("/").filter(Boolean)].filter(Boolean);
  if (parts.length === 0) return "/";
  const [head, ...rest] = parts;
  switch (head) {
    case "contact":
      return rest[0] ? `/contact/${encodeURIComponent(rest[0])}` : "/people";
    case "invite":
      return rest[0] ? `/invite/${encodeURIComponent(rest[0])}` : "/settings/workspace";
    case "oauth":
      // Integration OAuth returns are consumed by the host; nothing to show.
      return null;
    case "match":
      return rest[0] ? `/matches/${encodeURIComponent(rest[0])}` : "/matches";
    default:
      return `/${parts.map(encodeURIComponent).join("/")}${url.search}`;
  }
}

export function useHostEvents() {
  const navigate = useNavigate();
  useEffect(() => {
    const host = getHost();
    if (!host?.shell) return;
    const offs: (() => void)[] = [];

    offs.push(
      host.shell.onDeepLink((url) => {
        const route = routeForDeepLink(url);
        if (route) navigate(route);
      }),
    );

    if (host.shell.onMenuAction) {
      offs.push(
        host.shell.onMenuAction((action) => {
          switch (action) {
            case "newPerson":
              navigate("/people/new");
              break;
            case "search":
              navigate("/search");
              break;
            case "commandBar":
              useCommandBarStore.getState().setOpen(true);
              break;
            case "toggleSidebar":
              useLayoutStore.getState().toggleNav();
              break;
            case "sendFeedback":
              useLayoutStore.getState().setFeedbackOpen(true);
              break;
            case "settings":
              navigate("/settings");
              break;
            case "syncNow":
              // The host already asked the engine to sync; the sync status dot reflects progress.
              toast("Syncing");
              break;
            default:
              break;
          }
        }),
      );
    }

    if (host.shell.onPickedFile) {
      offs.push(
        host.shell.onPickedFile((file) => {
          const kind = file.integration === "linkedin_csv" ? "linkedin" : "csv";
          const content = kind === "linkedin" ? file.content.replace(/^[\s\S]*?(?=First Name,)/, "") : file.content;
          usePendingImport.getState().set({ name: file.name, content, kind });
          navigate("/settings/integrations");
        }),
      );
    }

    return () => offs.forEach((off) => off());
  }, [navigate]);
}
